import React, { useMemo } from 'react';

// FX-rate picker for package generation. Lets the user choose which
// from_currency→USD multiplier the generated package uses for its USD amounts
// and the displayed FX rate. Three sources, in precedence order:
//   1. A free-typed override rate (a number). Takes precedence; clears the date.
//   2. A pick from the available dated rates (date - rate - source).
//   3. The system default (the rate for the batch invoice date).
//
// "Override" means overriding the RATE VALUE (a number) — never the currency.
//
// The parent owns the value and decides what to send to the backend: when the
// user leaves the selection on the unchanged default, the parent treats fx as
// null (use the dynamic default). See onChange for the emitted shape.
//
// Props:
//   currency        — the batch's from-currency code (e.g. "EUR"). Display only.
//   defaultOption   — { rate_date, rate, source } the system would auto-use, or
//                     null (USD batch or no rate available).
//   currentOverride — { rate, rate_date } persisted on the batch, or null.
//                     `rate_date` is null for a previously free-typed override.
//   available       — list of { rate_date, rate, source }, most recent first.
//   value           — { fx_rate, fx_rate_date, isOverride } current selection.
//                     fx_rate is the override-input string (free text) when
//                     isOverride is true; otherwise the picked/default rate.
//   onChange(next)  — next = { fx_rate, fx_rate_date, isOverride }.
//                     • Picked a dated rate: fx_rate = that rate (number),
//                       fx_rate_date = that date, isOverride = false.
//                     • Typed an override: fx_rate = the raw input string,
//                       fx_rate_date = null, isOverride = true.
//
// A "free-typed override" is signalled by isOverride: true with a null date.
function fmtRate(rate) {
  if (rate == null || rate === '') return '';
  const num = typeof rate === 'number' ? rate : parseFloat(rate);
  if (Number.isNaN(num)) return String(rate);
  // Show enough precision for FX rates without trailing-zero noise.
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

export default function FxRatePicker({
  currency,
  defaultOption = null,
  currentOverride = null,
  available = [],
  value,
  onChange,
}) {
  const cur = currency || '—';

  // The override text input — non-empty when the user is overriding the rate
  // value (isOverride), so typing always takes precedence over the date pick.
  const overrideText = value && value.isOverride ? (value.fx_rate ?? '') : '';

  // Which available row (by rate_date) is currently selected. Only meaningful
  // when not overriding. The empty option maps to the system default.
  const selectedDate =
    value && !value.isOverride && value.fx_rate_date != null
      ? value.fx_rate_date
      : '';

  // The effective rate that will be used, for the "rate that will be used"
  // summary line. Override text wins; else the picked date's rate; else default.
  const effectiveRate = useMemo(() => {
    if (value && value.isOverride) {
      const num = parseFloat(value.fx_rate);
      return Number.isNaN(num) ? null : num;
    }
    if (selectedDate) {
      const row = available.find((r) => r.rate_date === selectedDate);
      if (row) return Number(row.rate);
    }
    return defaultOption ? Number(defaultOption.rate) : null;
  }, [value, selectedDate, available, defaultOption]);

  const handleSelectDate = (e) => {
    const picked = e.target.value;
    if (!onChange) return;
    if (!picked) {
      // Back to the system default. Parent treats this as "no override".
      onChange({
        fx_rate: defaultOption ? Number(defaultOption.rate) : null,
        fx_rate_date: defaultOption ? defaultOption.rate_date : null,
        isOverride: false,
      });
      return;
    }
    const row = available.find((r) => r.rate_date === picked);
    onChange({
      fx_rate: row ? Number(row.rate) : null,
      fx_rate_date: picked,
      isOverride: false,
    });
  };

  const handleOverrideInput = (e) => {
    const raw = e.target.value;
    if (!onChange) return;
    if (raw.trim() === '') {
      // Cleared the override — fall back to whatever date is selected (or the
      // default when none). Date stays as-is so the select keeps its choice.
      const row = selectedDate
        ? available.find((r) => r.rate_date === selectedDate)
        : null;
      onChange({
        fx_rate: row
          ? Number(row.rate)
          : defaultOption
          ? Number(defaultOption.rate)
          : null,
        fx_rate_date: selectedDate || null,
        isOverride: false,
      });
      return;
    }
    // Typing the override takes precedence — the date becomes null.
    onChange({ fx_rate: raw, fx_rate_date: null, isOverride: true });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="vat-control-label">FX rate (1 {cur} = USD)</span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
            fontWeight: 600,
            color: '#111827',
          }}
        >
          {effectiveRate != null
            ? `1 ${cur} = ${fmtRate(effectiveRate)} USD`
            : 'No rate available'}
        </span>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span className="vat-control-label">Rate date</span>
        <select
          className="status-select"
          value={selectedDate}
          onChange={handleSelectDate}
          disabled={!!overrideText || available.length === 0}
        >
          <option value="">
            {defaultOption
              ? `Default — ${defaultOption.rate_date} · ${fmtRate(
                  defaultOption.rate
                )} · ${defaultOption.source}`
              : 'System default'}
          </option>
          {available.map((row) => {
            const isDefault =
              defaultOption && row.rate_date === defaultOption.rate_date;
            return (
              <option key={row.rate_date} value={row.rate_date}>
                {row.rate_date} · {fmtRate(row.rate)} · {row.source}
                {isDefault ? ' (default)' : ''}
              </option>
            );
          })}
        </select>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span className="vat-control-label">
          Override rate (1 {cur} = ___ USD)
        </span>
        <input
          type="number"
          step="any"
          min="0"
          inputMode="decimal"
          className="status-select"
          value={overrideText}
          onChange={handleOverrideInput}
          placeholder="Type to override the rate value"
        />
      </label>

      <p style={{ fontSize: 11.5, color: '#6b7280', margin: 0 }}>
        {overrideText
          ? `Using your override rate. Override sets the rate value (a number), not the currency (${cur}).`
          : currentOverride
          ? 'Leaving this on the saved rate keeps the package using it.'
          : 'Leaving this on the default uses the rate for the batch invoice date.'}
      </p>
    </div>
  );
}
